import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SessionTracker from "@/components/SessionTracker";
import { Plus, Search, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const fmtGs = (n) =>
  new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG", maximumFractionDigits: 0 }).format(n || 0);

export default function Pacientes() {
  const [pacientes, setPacientes] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [q, setQ] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    try {
      const [p, pr] = await Promise.all([api.get("/pacientes"), api.get("/profesionales")]);
      setPacientes(p.data);
      setProfesionales(pr.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = pacientes.filter((p) =>
    `${p.nombre} ${p.apellido} ${p.dni || ""}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1400px]">
      <PageHeader
        eyebrow="Pacientes"
        title="Historia clínica"
        description="Listado de pacientes con progreso del tratamiento (8 sesiones) y estado de pagos."
        action={
          <button data-testid="nuevo-paciente-btn" onClick={() => setModalOpen(true)} className="btn-primary-pill">
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo paciente
          </button>
        }
      />

      <div className="card-clinic p-3 mb-6">
        <div className="flex items-center gap-2 px-2">
          <Search className="w-4 h-4 text-neutral-400" />
          <input
            data-testid="paciente-search"
            className="w-full bg-transparent outline-none py-2 text-sm placeholder:text-neutral-400"
            placeholder="Buscar por nombre, apellido o CI"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="card-clinic overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50/50">
              {["Paciente", "Profesional", "Progreso", "Costo / Pagado", "Estado", ""].map((h, i) => (
                <th key={i} className="overline text-left px-5 py-3.5 text-neutral-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-14 text-sm text-neutral-500">
                  No hay pacientes cargados. Comenzá agregando uno.
                </td>
              </tr>
            )}
            {filtered.map((p) => {
              const prof = profesionales.find((x) => x.id === p.profesional_id);
              return (
                <tr key={p.id} className="border-b border-neutral-200 row-hover" data-testid={`paciente-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-neutral-900">
                      {p.nombre} {p.apellido}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {p.dni ? `CI ${p.dni}` : "Sin CI"} · {p.telefono || "Sin teléfono"}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {prof ? (
                      <span className="inline-flex items-center gap-2 text-sm text-neutral-700">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: prof.color || "#6B8E6B" }}
                        />
                        {prof.name}
                      </span>
                    ) : (
                      <span className="text-sm text-neutral-400">Sin asignar</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <SessionTracker completed={p.sesiones_completadas} total={p.sesiones_totales} />
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="text-neutral-900 font-semibold">{fmtGs(p.costo_tratamiento)}</div>
                    <div
                      className={`text-xs ${
                        p.saldo_pendiente > 0 ? "text-[hsl(var(--terracotta))]" : "text-[hsl(var(--sage-hover))]"
                      }`}
                    >
                      {p.saldo_pendiente > 0
                        ? `Saldo ${fmtGs(p.saldo_pendiente)}`
                        : `Pagado ${fmtGs(p.total_pagado)}`}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        p.estado === "activo"
                          ? "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]"
                          : "bg-neutral-100 text-neutral-500"
                      }`}
                    >
                      {p.estado}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link
                      to={`/pacientes/${p.id}`}
                      data-testid={`ver-paciente-${p.id}`}
                      className="text-[hsl(var(--sage-hover))] text-sm font-medium inline-flex items-center gap-1 hover:underline"
                    >
                      Ver ficha <ArrowRight className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <PacienteModal
          profesionales={profesionales}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function PacienteModal({ onClose, onSaved, profesionales }) {
  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    dni: "",
    telefono: "",
    email: "",
    fecha_nacimiento: "",
    direccion: "",
    notas: "",
    profesional_id: profesionales[0]?.id || "",
    costo_tratamiento: 0,
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nombre || !form.apellido) {
      toast.error("Nombre y apellido son obligatorios");
      return;
    }
    setLoading(true);
    try {
      await api.post("/pacientes", { ...form, costo_tratamiento: Number(form.costo_tratamiento) || 0 });
      toast.success("Paciente creado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-2xl border border-neutral-200 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 sticky top-0 bg-white">
          <div>
            <div className="overline">Nuevo</div>
            <h3 className="text-lg font-bold tracking-tight">Cargar paciente</h3>
          </div>
          <button data-testid="paciente-modal-close" onClick={onClose} className="btn-ghost-pill p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nombre" testId="paciente-nombre" value={form.nombre} onChange={(v) => set("nombre", v)} />
          <Field label="Apellido" testId="paciente-apellido" value={form.apellido} onChange={(v) => set("apellido", v)} />
          <Field label="CI" testId="paciente-dni" value={form.dni} onChange={(v) => set("dni", v)} />
          <Field label="Teléfono" testId="paciente-telefono" value={form.telefono} onChange={(v) => set("telefono", v)} />
          <Field label="Email" testId="paciente-email" type="email" value={form.email} onChange={(v) => set("email", v)} />
          <Field
            label="Fecha de nacimiento"
            testId="paciente-fnac"
            type="date"
            value={form.fecha_nacimiento}
            onChange={(v) => set("fecha_nacimiento", v)}
          />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Profesional asignado</label>
            <select
              data-testid="paciente-profesional"
              className="input-clinic"
              value={form.profesional_id}
              onChange={(e) => set("profesional_id", e.target.value)}
            >
              <option value="">Sin asignar</option>
              {profesionales.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Costo del tratamiento (8 sesiones)"
            testId="paciente-costo"
            type="number"
            value={form.costo_tratamiento}
            onChange={(v) => set("costo_tratamiento", v)}
          />
          <Field label="Dirección" testId="paciente-direccion" value={form.direccion} onChange={(v) => set("direccion", v)} />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Notas</label>
            <textarea
              data-testid="paciente-notas"
              className="input-clinic min-h-[80px]"
              value={form.notas}
              onChange={(e) => set("notas", e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-neutral-200 bg-neutral-50/50 rounded-b-2xl sticky bottom-0">
          <button onClick={onClose} className="btn-outline-pill">
            Cancelar
          </button>
          <button data-testid="paciente-guardar" onClick={save} disabled={loading} className="btn-primary-pill">
            {loading ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1.5">{label}</label>
      <input
        data-testid={testId}
        type={type}
        className="input-clinic"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
